import React from 'react';
import { Cpu, FlaskConical, Truck, Users, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

export const TechnologyPage = () => {
  const { t, direction } = useLanguage();

  const technologies = [
    {
      id: 1,
      icon: Cpu,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      image: 'https://customer-assets.emergentagent.com/job_hazard-guard-1/artifacts/vkg0khtr_ChatGPT%20Image%20Feb%2017%2C%202026%2C%2002_23_10%20PM.png'
    },
    {
      id: 2,
      icon: FlaskConical,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      image: 'https://customer-assets.emergentagent.com/job_hazard-guard-1/artifacts/ix1svoii_ChatGPT%20Image%20Feb%2017%2C%202026%2C%2001_47_20%20PM.png'
    },
    {
      id: 3,
      icon: Truck,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      image: 'https://customer-assets.emergentagent.com/job_hazard-guard-1/artifacts/we8am3o5_Screenshot%202026-02-17%20143658.png'
    },
    {
      id: 4,
      icon: Users,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      image: 'https://customer-assets.emergentagent.com/job_hazard-guard-1/artifacts/26qjxzcv_Screenshot%202026-02-17%20143947.png'
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white" dir={direction}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            <ArrowLeft className="w-4 h-4" />
            {direction === 'rtl' ? 'العودة للرئيسية' : direction === 'en' ? 'Back to Home' : 'Retour à l\'accueil'}
          </Button>
        </div>

        {/* Header */}
        <div className={`text-center mb-16 ${direction === 'rtl' ? 'rtl' : ''}`}>
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Cpu className="w-4 h-4" />
            {t('about.techHeader')}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('about.techTitle')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('about.techSubtitle')}
          </p>
        </div>

        {/* Odoo Partner Badge */}
        <div className="flex justify-center mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center gap-6 border border-gray-100 max-w-xl">
            <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-purple-100 flex items-center justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_hazard-guard-1/artifacts/jk7qlebv_ChatGPT%20Image%20Feb%2017%2C%202026%2C%2002_35_22%20PM.png"
                alt="Odoo Partner"
                className="w-20 h-20 object-contain"
              />
            </div>
            <div className={direction === 'rtl' ? 'text-right' : ''}>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('about.odooPartner')}</h3>
              <p className="text-gray-600 text-sm">
                {t('about.tech1Desc')}
              </p>
            </div>
          </div>
        </div>

        {/* Technology Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {technologies.map((tech, index) => {
            const IconComponent = tech.icon;
            return (
              <div
                key={tech.id}
                className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 ${
                  direction === 'rtl' ? 'text-right' : ''
                }`}
              >
                <div className={`h-2 bg-gradient-to-r ${tech.color}`}></div>
                <div className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`w-14 h-14 rounded-xl ${tech.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <IconComponent className={`w-7 h-7 ${tech.iconColor}`} />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-blue-600 mb-1">{t('about.innovation')} #{index + 1}</div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {t(`about.tech${index + 1}Title`)}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {t(`about.tech${index + 1}Desc`)}
                  </p>

                  {tech.image && (
                    <div className="rounded-xl overflow-hidden bg-gray-100">
                      <img
                        src={tech.image}
                        alt={t(`about.tech${index + 1}Title`)}
                        className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Traceability Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-8 md:p-12 text-white">
          <div className={`flex flex-col md:flex-row items-center gap-8 ${direction === 'rtl' ? 'md:flex-row-reverse' : ''}`}>
            <div className="flex-1">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">{t('about.traceability')}</h3>
              <p className="text-blue-100 text-lg leading-relaxed">
                {t('about.traceabilityDesc')}
              </p>
            </div>
            <div className="flex-shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_hazard-guard-1/artifacts/ix1svoii_ChatGPT%20Image%20Feb%2017%2C%202026%2C%2001_47_20%20PM.png"
                alt="Traceability"
                className="w-64 h-48 object-cover rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          {[
            { label: t('about.statsAI'), value: 'ERP', icon: Cpu, color: 'text-blue-600' },
            { label: t('about.statsQuality'), value: '100%', icon: FlaskConical, color: 'text-purple-600' },
            { label: t('about.statsGPS'), value: 'GPS', icon: Truck, color: 'text-orange-600' },
            { label: t('about.statsClients'), value: '200+', icon: Users, color: 'text-green-600' }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center border border-gray-100 hover:shadow-xl transition-shadow">
              <stat.icon className={`w-8 h-8 ${stat.color} mx-auto mb-3`} />
              <div className={`text-3xl font-bold ${stat.color} mb-1`}>{stat.value}</div>
              <div className="text-gray-600 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Button
            onClick={() => window.location.href = '/#partenaires'}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            {t('nav.partners')}
          </Button>
        </div>
      </div>
    </section>
  );
};
